import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatMoney, type JobResponse, type PendingOffer } from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { quoteKeys } from '../quotes/quotes-ui.js';
import { jobKeys } from './jobs-ui.js';

/**
 * An accepted offer into its commessa (16/10/2026): a new one, «in
 * preparazione», or one already open of the same client.
 */
export function LinkOfferDialog({
  offer,
  onClose,
  onLinked,
}: {
  offer: PendingOffer;
  onClose: () => void;
  onLinked: (job: JobResponse) => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [mode, setMode] = useState<'new' | 'existing'>(offer.openJobs.length ? 'existing' : 'new');
  const [jobId, setJobId] = useState(offer.openJobs[0]?.id ?? '');
  const [name, setName] = useState(offer.referenceName ?? '');
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const link = useMutation({
    mutationFn: () =>
      apiRequest<JobResponse>(`/jobs/offers/${offer.quoteId}/link`, {
        method: 'POST',
        body: mode === 'new' ? { jobId: null, newJobName: name } : { jobId, newJobName: null },
      }),
    onSuccess: async (job) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: jobKeys.all }),
        queryClient.invalidateQueries({ queryKey: quoteKeys.all }),
      ]);
      onLinked(job);
      onClose();
    },
  });
  const ready = mode === 'new' ? name.trim().length > 0 : Boolean(jobId);

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>Collega l’offerta {offer.label}</h2>
        <p className="muted small">
          {offer.clientName} · del {formatDate(offer.date)}
          {offer.owner && ` · commerciale ${offer.owner.name}`} · {formatMoney(offer.net)}
        </p>
        <label className="checkbox">
          <input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} /> Nuova
          commessa
        </label>
        {mode === 'new' && (
          <Field
            label="Nome della commessa"
            hint="Nasce «in preparazione»: PM, Account e date si completano nella scheda."
          >
            <input value={name} onChange={(event) => setName(event.target.value)} maxLength={200} />
          </Field>
        )}
        <label className="checkbox">
          <input
            type="radio"
            checked={mode === 'existing'}
            disabled={offer.openJobs.length === 0}
            onChange={() => setMode('existing')}
          />{' '}
          Commessa già aperta di {offer.clientName}
        </label>
        {offer.openJobs.length === 0 && (
          <p className="muted small">Il cliente non ha commesse aperte.</p>
        )}
        {mode === 'existing' && (
          <Field label="Commessa">
            <select value={jobId} onChange={(event) => setJobId(event.target.value)}>
              {offer.openJobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.code} · {job.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        {offer.owner && (
          <p className="notice info small">
            {offer.owner.name} diventa il commerciale della commessa, con «vede la commessa» spenta:
            la accendi dalla scheda, se serve.
          </p>
        )}
        {link.isError && <p className="error">{link.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={link.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={!ready || link.isPending}
            onClick={() => link.mutate()}
          >
            {link.isPending ? 'Attendere…' : 'Collega'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
